import { ExternalLink } from '@components/ExternalLink'
import SBNiceDate from '@components/utils/SBNiceDate'
import { Subscription as SubscriptionModel } from '@models/membership/Subscription'
import { SubscriptionPayment } from '@models/membership/SubscriptionPayment'
import { fetchSubscriptionPayments } from '@services/membership/PaymentService'
import { useAppSelector } from '@store/hooks'
import { selectSubscriptionById } from '@store/membership/SubscriptionReducer'
import { EllipsisMiddle } from '@supplement-bacon/react-toolkit/components'
import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { currencyFormat } from '@utils/currencyFormatter'
import {
  Badge,
  Card,
  Descriptions,
  Empty,
  Flex,
  Space,
  Spin,
  Timeline,
  Tooltip,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { FC, useEffect, useState } from 'react'

const { Title, Text } = Typography

type Props = SBModelComponent<SubscriptionModel> & {}

const SubscriptionPaymentData: FC<Props> = ({ id, model }) => {
  const item = model ?? useAppSelector(selectSubscriptionById(id!))

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [payments, setPayments] = useState<SubscriptionPayment[]>([])

  useEffect(() => {
    if (!item) return

    setIsLoading(true)
    fetchSubscriptionPayments({
      ...initialQuery,
      filters: {
        subscriptions: [item.id],
      },
      columnKey: 'date',
      order: 'descend',
    })
      .then((result) => setPayments(result.data))
      .finally(() => setIsLoading(false))
  }, [item])

  if (!item) {
    return <></>
  }

  return (
    <Flex vertical gap={8}>
      <Title level={5} className="m-0">
        Payment history
      </Title>
      <Card styles={{ body: { padding: 16, paddingTop: 20 } }}>
        {isLoading && <Spin />}
        {payments.length === 0 ? (
          <Empty
            description="No payments found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Timeline
            items={payments.map((payment) => ({
              color: payment.completedAt ? 'green' : 'orange',
              children: (
                <Flex vertical style={{ marginBottom: 20 }}>
                  <Flex justify="space-between" align="center">
                    <Text strong>{currencyFormat(payment.amount, true)}</Text>
                    <Text type="secondary">
                      {dayjs(payment.createdAt).format('DD/MM/YYYY HH:mm')}
                    </Text>
                  </Flex>
                  <Descriptions
                    bordered
                    column={1}
                    size="small"
                    style={{ marginTop: 12 }}
                  >
                    <Descriptions.Item label="PSP Reference">
                      <Space>
                        <Text strong copyable>
                          {payment.pspReference}
                        </Text>
                        {payment.pspUrl && (
                          <Tooltip title="See payment within your Payment Service Provider">
                            <>
                              <ExternalLink to={payment.pspUrl} content="" />
                            </>
                          </Tooltip>
                        )}
                      </Space>
                    </Descriptions.Item>

                    <Descriptions.Item label="Status">
                      <Badge
                        status={
                          payment.failedAt
                            ? 'error'
                            : payment.completedAt
                              ? 'success'
                              : 'processing'
                        }
                        text={
                          payment.failedAt
                            ? 'Failed'
                            : payment.completedAt
                              ? 'Completed'
                              : 'Pending'
                        }
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Last update at">
                      <SBNiceDate date={payment.updatedAt} />
                    </Descriptions.Item>

                    {payment.completedAt && (
                      <Descriptions.Item label="Completed at">
                        <SBNiceDate date={payment.completedAt} />
                      </Descriptions.Item>
                    )}

                    <Descriptions.Item label="Raw amount (cents)">
                      <Text>{payment.amount}</Text>
                    </Descriptions.Item>

                    <Descriptions.Item label="Plethore ID">
                      <EllipsisMiddle
                        suffixCount={5}
                        copyable={{ text: payment.id }}
                      >
                        {payment.id}
                      </EllipsisMiddle>
                    </Descriptions.Item>

                    {payment.failureReason && (
                      <Descriptions.Item label="Failure Reason">
                        <Text>{payment.failureReason}</Text>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Flex>
              ),
            }))}
          />
        )}
      </Card>
    </Flex>
  )
}

type SubscriptionPaymentDataType = {
  PaymentData: typeof SubscriptionPaymentData
}

export { SubscriptionPaymentData, type SubscriptionPaymentDataType }
